import React, { useMemo } from "react";
import { Workflow } from "../../../../../services/WorkflowService";
import ModelSelect from "../../../../configuration/ModelSelect";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Label } from "../../../../ui/label";
import { Button } from "../../../../ui/button";
import { Textarea } from "../../../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../ui/select";
import { CpuIcon, BrainIcon, LayersIcon, EyeIcon, SparklesIcon, RefreshCwIcon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  GenerationConfig,
  AgentItem,
  mergePlaceholdersWithGenerationVars,
  buildMergedTeacherWorkflowInput,
  getTeacherWorkflowInputSchema,
} from "./types";
import {
  IncludeReasoningToggle,
  ResponseSchemaBuilder,
  MaxTokensInput,
  ResponseExampleField,
  TemplatePlaceholderBadges,
} from "./SharedFields";
interface StepResponsesProps {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
  workflows: Workflow[];
  agents: AgentItem[];
}

const TEACHER_OPTIONS = [
  { id: "model" as const, label: "Model", desc: "Direct LLM call" },
  { id: "workflow" as const, label: "Workflow", desc: "Maitai Workflow" },
  { id: "agent" as const, label: "Agent", desc: "Maitai Agent" },
];

const StepResponses: React.FC<StepResponsesProps> = ({ config, onChange, workflows, agents }) => {
  const allPlaceholders = useMemo(
    () =>
      mergePlaceholdersWithGenerationVars(
        config.generation_request_system_prompt || "",
        config.generation_user_template || "",
        config.generation_variables,
        config.generation_computed_placeholders
      ),
    [
      config.generation_request_system_prompt,
      config.generation_user_template,
      config.generation_variables,
      config.generation_computed_placeholders,
    ]
  );

  const isModelTeacher = config.generation_teacher_type === "model";

  const teacherInputSchema = useMemo(
    () => getTeacherWorkflowInputSchema(workflows, config.generation_model),
    [workflows, config.generation_model]
  );

  const defaultWorkflowInputJson = useMemo(() => {
    return buildMergedTeacherWorkflowInput(allPlaceholders, teacherInputSchema);
  }, [allPlaceholders, teacherInputSchema]);

  const showWorkflowInput =
    config.generation_teacher_type === "workflow" &&
    (allPlaceholders.length > 0 || Boolean(teacherInputSchema && Object.keys(teacherInputSchema).length > 0));

  const workflowValuePath =
    config.generation_teacher_type === "workflow"
      ? {
          value: config.generation_teacher_response_value_path || "",
          onPathChange: (path: string) => onChange({ generation_teacher_response_value_path: path }),
        }
      : undefined;

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <CpuIcon className="w-5 h-5 text-maitai-green" />
            Teacher
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            The teacher generates the response for each prompt. Choose a model, workflow, or agent.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {TEACHER_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange({ generation_teacher_type: opt.id, generation_model: "" })}
                className={cn(
                  "p-3 rounded-lg border-2 text-left transition-colors",
                  config.generation_teacher_type === opt.id
                    ? "border-maitai-green bg-maitai-green/5"
                    : "border-border hover:border-maitai-green/50"
                )}
              >
                <div className="text-sm font-gc-med text-foreground">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
              </button>
            ))}
          </div>

          {config.generation_teacher_type === "model" && (
            <ModelSelect
              value={config.generation_model}
              onChange={(value) => onChange({ generation_model: value })}
              placeholder="Select a teacher model"
              label=""
              showEmptyOption={false}
              modelType="server"
            />
          )}

          {config.generation_teacher_type === "workflow" && (
            <Select value={config.generation_model || undefined} onValueChange={(v) => onChange({ generation_model: v })}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select a workflow" /></SelectTrigger>
              <SelectContent>
                {workflows.map((wf) => (
                  <SelectItem key={wf.id} value={wf.workflow_ref_name}>{wf.workflow_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {config.generation_teacher_type === "agent" && (
            <Select value={config.generation_model || undefined} onValueChange={(v) => onChange({ generation_model: v })}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select an agent" /></SelectTrigger>
              <SelectContent>
                {agents.map((ag) => (
                  <SelectItem key={ag.id} value={String(ag.id)}>{ag.agent_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {showWorkflowInput && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <LayersIcon className="w-5 h-5 text-maitai-green" />
                Workflow input to teacher
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  try {
                    let raw = config.generation_workflow_input || defaultWorkflowInputJson;
                    const sentinel = (k: string) => `"__PH_${k}__"`;
                    for (const p of allPlaceholders) {
                      raw = raw.replaceAll(`"{{${p}}}"`, sentinel(p));
                      raw = raw.replaceAll(`{{${p}}}`, sentinel(p));
                    }
                    let tidy = JSON.stringify(JSON.parse(raw), null, 2);
                    for (const p of allPlaceholders) {
                      tidy = tidy.replaceAll(sentinel(p), `{{${p}}}`);
                    }
                    onChange({ generation_workflow_input: tidy });
                  } catch { /* invalid JSON */ }
                }}
              >
                <SparklesIcon className="w-3 h-3" /> Tidy
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              JSON sent to the workflow as <code className="text-xs bg-muted px-1 rounded">ctx.input</code>.
              Keys match the workflow&apos;s input schema when it defines one; each value is typically a{" "}
              <code className="text-xs bg-muted px-1 rounded">{"{{placeholder}}"}</code>{" "}
              filled from your prompts and variables at generation time.
            </p>
            <Textarea
              value={config.generation_workflow_input || defaultWorkflowInputJson}
              onChange={(e) => onChange({ generation_workflow_input: e.target.value })}
              rows={Math.max(4, allPlaceholders.length + 2)}
              className="font-mono text-xs"
            />
            <TemplatePlaceholderBadges placeholders={allPlaceholders} color="blue" />
            <p className="text-xs text-muted-foreground">
              Configure seed vs computed variables in the Prompts step. Choosing a teacher workflow merges schema
              fields with your variable names; new variables add keys here automatically when you save them.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange({ generation_workflow_input: defaultWorkflowInputJson })}
              className="gap-1.5"
            >
              <RefreshCwIcon className="w-3.5 h-3.5" /> Reset to default
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <LayersIcon className="w-5 h-5 text-maitai-green" />
            Response
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isModelTeacher && (
            <div className="space-y-3 pb-4 border-b border-border">
              <div className="flex items-center gap-2">
                <BrainIcon className="w-4 h-4 text-maitai-green" />
                <Label className="text-sm font-gc-med text-foreground">
                  Teacher system prompt <span className="text-xs text-muted-foreground font-gc-reg">(optional)</span>
                </Label>
              </div>
              <Textarea
                value={config.generation_teacher_system_prompt}
                onChange={(e) => onChange({ generation_teacher_system_prompt: e.target.value })}
                placeholder="e.g. You are a precise classifier. Follow the response format exactly."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Sent to the teacher model with each call (not stored on the dataset examples by itself).
                Use the Prompts step for the system message that appears on each training example.
              </p>
            </div>
          )}

          <IncludeReasoningToggle value={config.generation_include_reasoning} onChange={onChange} />

          <div className="pt-4 border-t border-border">
            <ResponseSchemaBuilder
              schema={config.generation_response_schema}
              onChange={onChange}
              workflowTeacherResponseValuePath={workflowValuePath}
            />
          </div>

          {isModelTeacher && (
            <div className="pt-4 border-t border-border">
              <MaxTokensInput value={config.generation_max_tokens} onChange={onChange} />
            </div>
          )}

          <div className="pt-4 border-t border-border space-y-3">
            <div className="flex items-center gap-2">
              <EyeIcon className="w-4 h-4 text-maitai-green" />
              <Label className="text-sm font-gc-med text-foreground">
                Example response <span className="text-xs text-muted-foreground font-gc-reg">(optional)</span>
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              A concrete answer the teacher can use as a style and format reference.
            </p>
            <ResponseExampleField
              value={config.generation_response_example}
              schema={config.generation_response_schema}
              onChange={onChange}
              showLabel={false}
            />
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default StepResponses;
